import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../components/AuthContext/AuthContext";
import { BoxArrowInRight, XSquare, CheckCircleFill, XCircleFill } from "react-bootstrap-icons";
import { useLocation, Navigate } from "react-router-dom";
import useApis from "../../services/useAPI";
import { Spinner } from "react-bootstrap";
import PropTypes from "prop-types";
import AccountCard from "../../stories/cards/AccountCard";
import { useMediaQuery } from "react-responsive";

let redirect;

const ConfirmAccount = ({ email, formData }) => {
  const auth = useAuth();
  const location = useLocation();
  const resendUrl = `/api/dashboard/resend-activation-email`;
  const [redirect, setRedirect] = useState(null);

  // Get email from URL query parameters
  const searchParams = new URLSearchParams(location.search);
  const emailFromQuery = searchParams.get('email');
  
  // Also check location state for email
  const emailFromState = location.state?.email;
  
  // Use email from props, query param, or state
  const initialEmail = email || emailFromQuery || emailFromState || "";
  
  const act = location.pathname.split("/")[2];
  const token = location.pathname.split("/")[3];
  const url = `/api/dashboard/activate-account/${act}/${token}`;
  const activateUrl = `/api/dashboard/verify-account`;
  
  const [loading, setLoading] = useState(false);
  const [accountEmail, setAccountEmail] = useState(initialEmail);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [otpSuccess, setOtpSuccess] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [canResend, setCanResend] = useState(true);
  const [resendTimer, setResendTimer] = useState(0);
  const inputRefs = useRef([]);
  const { callApi: resendCallApi } = useApis();
  const apiCalledRef = useRef(false);
  const [isExpired, setIsExpired] = useState(false);
  const [showResendPage, setShowResendPage] = useState(false);
  const [isToken, setIsToken] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const isPhlebotomy = localStorage.getItem("is_phlebotomist");
  const OT_INPUT_IDS = ['otp-0', 'otp-1', 'otp-2', 'otp-3', 'otp-4', 'otp-5'];
  const testType = localStorage.getItem("test_type");
  const autoVerifyTimeoutRef = useRef(null);

  // Check if next parameter contains bloods/boots pattern
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const nextPath = searchParams.get("next");
    if (nextPath?.includes("bloods/boots")) {
      localStorage.setItem("is_phlebotomist", "true");
    }
  }, [location.search]);

  useEffect(() => {
    setRedirect(null);

    if (token) {
      // FLOW 2: Has token
      if (!apiCalledRef.current) {
        apiCalledRef.current = true;
        setLoading(true);
        (async () => {
          try {
            const response = await fetch(url, {
              credentials: "include",
              headers: { Accept: "application/json" },
            });
            const data = await response.json();

            // FLOW 2.3: is_expired true → resend page
            if (data?.data?.is_expired === true) {
              
              auth.showToast(
                  "Expired",
                  "This link has expired. Please click the resend confirmation email button",
                  "../assets/mhc.png",
                  <XSquare />,
                );
              setIsExpired(true);
              setShowResendPage(true);
              setLoading(false);
              return;
            }

            // FLOW 2.1: success → go to /verify
            if (data?.data?.jwt_token) {
              auth.setTokens(
                data.data.jwt_token,
                data.data.email,
                data.data.role,
              );
              auth.setIsAuthenticated(true);
              localStorage.setItem("user_id", data.data.id);
              localStorage.setItem("username", data.data.fullname);
              localStorage.setItem("defered_mfa", true);
              auth.showToast(
                "Confirmed",
                data?.message,
                "../assets/mhc.png",
                <BoxArrowInRight />,
              );
              
              setRedirect(isPhlebotomy === "true" ? `/bloods/boots/${testType}` : "/verify");
              setLoading(false);
              return;
            }

            // FLOW 2.2: "Invalid or expired activation link." → go to login
            if (data?.error === "Invalid or expired activation link.") {
               auth.showToast(
                  "Activated",
                  "Account activated already please login",
                  "../assets/mhc.png",
                  <XSquare />,
                );
                setRedirect("/login");
              setLoading(false);
              return;
            }

            // ← any other error → resend page
            if (data?.error) {
              setShowResendPage(true);
              setLoading(false);
              return;
            }

            // ← any other unexpected response → resend page
            setShowResendPage(true);
            setLoading(false);

          } catch (err) {
            // ← network error → resend page
            setShowResendPage(true);
            setLoading(false);
          }
        })();
      }
    } else {
      // FLOW 1: No token
      if (apiCalledRef.current) return;
        apiCalledRef.current = true;
        setLoading(true);
        (async () => {
          if (initialEmail) {
            // ← email exists from props, query, or state, use directly
            setAccountEmail(initialEmail);
            setShowResendPage(true);
            setLoading(false);
          } else {
            // ← no email, fetch from API
            try {
              const response = await fetch(url, {
                credentials: "include",
                headers: { Accept: "application/json" },
              });
              const data = await response.json();

              // ← invalid link error → go to login
              if (data?.error === "Invalid or expired activation link." ) {
                 auth.showToast(
                  "Activated",
                  "Account activated already please login",
                  "../assets/mhc.png",
                  <XSquare />,
                );
                setRedirect("/login");
                setLoading(false);
                return;
              }

              // ← any other error → resend page
              if (data?.error) {
                setAccountEmail(data?.data?.email || "");
                setShowResendPage(true);
                setLoading(false);
                return;
              }
                auth.showToast(
                  "Expired",
                  "This link has expired. Please click the resend confirmation email button",
                  "../assets/mhc.png",
                  <XSquare />,
                );

              setAccountEmail(data?.data?.email || "");
              setShowResendPage(true);
              setLoading(false);
              setIsToken(true);

            } catch (err) {
              setShowResendPage(true);
              setLoading(false);
            }
          }
        })();
      
    }
  }, [token, initialEmail]);

  // Auto-focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Timer for resend button
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Auto-verify OTP when 6 digits are entered
  useEffect(() => {
    const otpString = otp.join("");
    if (otpString.length === 6 && !otpSuccess && !loading) {
      // Clear any pending timeout
      if (autoVerifyTimeoutRef.current) {
        clearTimeout(autoVerifyTimeoutRef.current);
      }
      // Small delay to allow last digit animation
      autoVerifyTimeoutRef.current = setTimeout(() => {
        handleVerifyOtp();
      }, 100);
    }
    
    return () => {
      if (autoVerifyTimeoutRef.current) {
        clearTimeout(autoVerifyTimeoutRef.current);
      }
    };
  }, [otp]);

  // Handle input change
  const handleInputChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    // Reset success/error states when user starts typing again
    if (otpSuccess) {
      setOtpSuccess(false);
    }
    if (otpError) {
      setOtpError("");
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      const newOtp = [...otp];
      if (otp[index]) {
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        newOtp[index - 1] = "";
        setOtp(newOtp);
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    if (!/^\d*$/.test(pastedData)) return;

    const newOtp = pastedData.split("");
    while (newOtp.length < 6) {
      newOtp.push("");
    }
    setOtp(newOtp.slice(0, 6));
    setOtpError("");
    setOtpSuccess(false);

    // Focus on the next empty or last filled position
    const nextEmptyIndex = newOtp.findIndex((val) => !val);
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    setTimeout(() => inputRefs.current[focusIndex]?.focus(), 0);
  };

  const otpString = otp.join("");
  const isValidOtp = otpString.length === 6;

  // Handle OTP submission
  const handleVerifyOtp = async (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    if (!accountEmail) {
      setOtpError("Email is required");
      return;
    }

    if (!isValidOtp) {
      setOtpError("Please enter a valid 6-digit OTP");
      return;
    }

    setLoading(true);
    setOtpError("");
    setOtpSuccess(false);

    try {
      const response = await fetch(activateUrl, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          email: accountEmail,
          otp: otpString,
        }),
      });

      const data = await response.json();

      // Success - activate account
      if (data?.data?.jwt_token) {
        auth.setTokens(
          data.data.jwt_token,
          data.data.email,
          data.data.role,
        );
        auth.setIsAuthenticated(true);
        localStorage.setItem("user_id", data.data.id);
        localStorage.setItem("username", data.data.fullname);
        localStorage.setItem("is_logged_in_first_time", data?.data?.is_logged_in_first_time);
        localStorage.setItem("defered_mfa", true);
        auth.showToast(
          "Confirmed",
          data?.message || "Account activated successfully",
          "../assets/mhc.png",
          <BoxArrowInRight />,
        );
        
        // Set success state for UI
        setOtpSuccess(true);
        return;
      }

      // Invalid/expired OTP
      if (data?.data?.is_expired === true) {
        setOtpError("Invalid OTP. Please try again.");
        auth.showToast(
          "Expired",
          "Invalid OTP. Please try again",
          "../assets/mhc.png",
          <XSquare />,
        );
      } else if (data?.error) {
        setOtpError(data.error || "Invalid OTP. Please try again.");
      }

      setOtpSuccess(false);
      setLoading(false);
    } catch (error) {
      setOtpError("Failed to verify OTP. Please try again.");
      auth.showToast(
        "Error",
        "Failed to verify OTP",
        "../assets/mhc.png",
        <XSquare />,
      );
      setOtpSuccess(false);
      setLoading(false);
    }
  };

  // Handle resend OTP
  const handleResendOtp = async () => {
    if (!accountEmail) {
      auth.showToast(
        "Error",
        "Email is required",
        "../assets/mhc.png",
        <XSquare />,
      );
      return;
    }

    setResendLoading(true);

    try {
      const response = await fetch(resendUrl, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ email: accountEmail }),
      });

      const data = await response.json();

      // Rate limit error
      if (data?.status === "rate_limited") {
        auth.showToast(
          "Rate Limit Exceeded",
          data?.message || "Too many requests. Please try again later.",
          "../assets/mhc.png",
          <XSquare />,
        );
        setResendLoading(false);
        return;
      }

      // Success
      if (data?.success) {
        auth.showToast(
          "Resent",
          `A verification email with otp has been sent to ${accountEmail}`,
          "../assets/mhc.png",
          <BoxArrowInRight />,
        );
        setOtp(["", "", "", "", "", ""]);
        setOtpError("");
        setOtpSuccess(false);
        setCanResend(false);
        setResendTimer(60);
        inputRefs.current[0]?.focus();
      } else if (data?.error) {
        auth.showToast(
          "Error",
          data.error,
          "../assets/mhc.png",
          <XSquare />,
        );
      }
    } catch (error) {
      auth.showToast(
        "Error",
        "Failed to resend OTP",
        "../assets/mhc.png",
        <XSquare />,
      );
    } finally {
      setResendLoading(false);
    }
  };

  // Navigation handlers
  const handleContinueToRegister = () => {
    if (otpSuccess) {
      // Manually trigger redirect if not already redirected
      setRedirect(isPhlebotomy === "true" ? `/bloods/boots/${testType}` : "/register-kit");
    }
  };

  const handleGoToDashboard = () => {
    if (otpSuccess) {
      setRedirect("/");
    }
  };

  if (loading && !otpSuccess) {
    return (
      <div className="text-center">
        <Spinner
          animation="border"
          role="output"
          data-testid="spinner"
          variant="info"
          className="my-custom-spinner"
        />
      </div>
    );
  }

  const getResendContent = () => {
    if (resendLoading) {
      return (
        <>
          <Spinner
            as="span"
            animation="border"
            size="sm"
            role="status"
            aria-hidden="true"
            className="otp-spinner"
          />
          <span>Sending...</span>
        </>
      );
    }

    if (canResend) {
      return (
          <span>Resend Code</span>
      );
    }

    return (
        <span className="resend-timer">Resend in {resendTimer}s</span>
    );
  };

  if (redirect) {
    return <Navigate to={redirect} replace />;
  }

  return (
    <AccountCard
      className="justify-content-center"
      title="Enter Verification Code"
      isLoading={loading}
      finished={false}
      verifyPage={true}
    >
      <div className="otp-verification-container">
        <div className="otp-header">
          <p className="otp-subtitle ageo-medium">
            We've sent a 6-digit verification code to <br/> <span>{accountEmail}</span>
          </p>
        </div>

        <form onSubmit={(e) => e.preventDefault()} className="otp-form">
          <div className="otp-inputs-wrapper">
            <div className="otp-inputs-grid">
              {otp.map((digit, index) => (
                <div key={OT_INPUT_IDS[index]} className="otp-input-wrapper">
                  <input
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleInputChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    className={`otp-input ${digit ? "filled" : ""} ${
                      otpError ? "error" : ""
                    } ${otpSuccess ? "success" : ""}`}
                    placeholder=""
                    disabled={loading || otpSuccess}
                    autoComplete="one-time-code"
                  />
                  <div className="otp-input-underline"></div>
                </div>
              ))}
            </div>

            {/* Success Message */}
            {otpSuccess && (
              <div className="otp-success-message mx-auto">
                <CheckCircleFill size={18} />
                <span className="ageo-medium">Code verified successfully!</span>
              </div>
            )}

            {/* Error Message */}
            {otpError && !otpSuccess && (
              <div className="otp-error-message mx-auto">
                <XCircleFill size={18} />
                <span className="ageo-medium">{otpError}</span>
              </div>
            )}
          </div>
        </form>

        <div className="row justify-content-center">
          <div className="col-md-8 col-12 px-2">
            <button 
              className={`btn ${otpSuccess ? 'btn-green' : 'btn-disabled'} w-100 fs-16 ageo-medium first-btn`}
              onClick={handleContinueToRegister}
              disabled={!otpSuccess}
              style={{ opacity: !otpSuccess ? 0.5 : 1, cursor: !otpSuccess ? 'not-allowed' : 'pointer' }}
            >
              Verify & continue to register
            </button>
          </div>
          <div className="col-md-8 col-12 px-2">
            <button 
              className={`btn ${otpSuccess ? 'btn-white' : 'btn-disabled'} w-100 fs-16 ageo-medium`}
              onClick={handleGoToDashboard}
              disabled={!otpSuccess}
              style={{ opacity: !otpSuccess ? 0.5 : 1, cursor: !otpSuccess ? 'not-allowed' : 'pointer', border: '1px solid #ccc' }}
            >
              I don't have a test - Go to dashboard
            </button>
          </div>
        </div>

        <div className="otp-resend-section">
          <p className="otp-resend-text text-muted ageo-medium mb-0">
            Didn't receive the code? 
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={resendLoading || !canResend || loading || otpSuccess}
              className="otp-resend-btn px-0 mx-1 ageo-semibold"
            >
              {getResendContent()}
            </button>
          </p>
        </div>
      </div>
    </AccountCard>
  );
};

ConfirmAccount.propTypes = {
  email: PropTypes.string,
  formData: PropTypes.object,
};

ConfirmAccount.defaultProps = {
  email: "",
  formData: {},
};

export default ConfirmAccount;